import { useRef, useEffect, useCallback } from 'react';
import { getVisiblePathMidpoint } from '../utils/getVisiblePathMidpoint';

/**
 * Hook for managing handle position updates
 * 
 * PERFORMANCE: Updates positions via direct DOM manipulation to avoid React re-renders
 * during pan/zoom animations.
 */
const PAN_HANDLE_IDLE_MS = 100;

export function useHandlePositions({
  containerRef,
  layerRef,
  nodePositions,
  nodeDimensions,
  edges,
  initialPan,
  initialZoom,
  layoutSurfaceEpoch = 0,
}) {
  const handlesContainerRef = useRef(null);
  const decisionHandlesContainerRef = useRef(null);
  const edgeButtonsContainerRef = useRef(null);
  const dragHandlesContainerRef = useRef(null);
  
  // Store refs for position updates (avoid state to prevent re-renders)
  const panRef = useRef(initialPan || { x: 0, y: 0 });
  const zoomRef = useRef(initialZoom || 1);
  const settledPanRef = useRef(initialPan || { x: 0, y: 0 });
  const settledZoomRef = useRef(initialZoom || 1);
  const handleIdleRef = useRef(null);
  
  // Update refs when props change
  useEffect(() => {
    if (initialPan) panRef.current = initialPan;
    if (initialZoom) zoomRef.current = initialZoom;
  }, [initialPan, initialZoom]);
  
  // Convert canvas coordinates to screen coordinates
  const canvasToScreen = useCallback((canvasX, canvasY) => {
    return {
      x: canvasX * zoomRef.current + panRef.current.x,
      y: canvasY * zoomRef.current + panRef.current.y,
    };
  }, []);
  
  // Update all handle positions directly via DOM (no React state)
  const updateHandlePositions = useCallback(() => {
    if (!handlesContainerRef.current) return;
    
    const handles = handlesContainerRef.current.querySelectorAll('[data-task-id]');
    handles.forEach(handle => {
      const taskId = handle.dataset.taskId;
      const pos = nodePositions[taskId];
      const dims = nodeDimensions[taskId];
      if (!pos || !dims) return;
      
      const screenPos = handle.dataset.handle === 'before'
        ? canvasToScreen(pos.x + dims.width / 2, pos.y)
        : canvasToScreen(pos.x + dims.width / 2, pos.y + dims.height);
      handle.style.left = `${screenPos.x}px`;
      handle.style.top = `${screenPos.y}px`;
    });
  }, [nodePositions, nodeDimensions, canvasToScreen]);
  
  // Update all decision handle positions directly via DOM
  const updateDecisionHandlePositions = useCallback(() => {
    if (!decisionHandlesContainerRef.current) return;
    
    const groups = decisionHandlesContainerRef.current.querySelectorAll('[data-task-id]');
    groups.forEach(group => {
      const taskId = group.dataset.taskId;
      const pos = nodePositions[taskId];
      const dims = nodeDimensions[taskId];
      if (!pos || !dims) return;
      
      const screenPos = canvasToScreen(pos.x + dims.width / 2, pos.y + dims.height);
      group.style.left = `${screenPos.x}px`;
      group.style.top = `${screenPos.y}px`;
    });
  }, [nodePositions, nodeDimensions, canvasToScreen]);
  
  // Update all edge button positions directly via DOM
  const updateEdgeButtonPositions = useCallback(() => {
    if (!edgeButtonsContainerRef.current) return;
    
    const rect = containerRef?.current?.getBoundingClientRect();
    const containerRect = rect ? { width: rect.width, height: rect.height } : null;

    const buttons = edgeButtonsContainerRef.current.querySelectorAll('[data-edge-id]');
    buttons.forEach(button => {
      const edgeId = button.dataset.edgeId;
      const edge = edges.find(e => e.id === edgeId);
      if (!edge) return;
      
      const midpoint = getVisiblePathMidpoint(
        edge.pathD,
        panRef.current,
        zoomRef.current,
        containerRect,
      );
      if (!midpoint) return;

      const screenPos = canvasToScreen(midpoint.x, midpoint.y);
      button.style.left = `${screenPos.x}px`;
      button.style.top = `${screenPos.y}px`;
    });
  }, [edges, canvasToScreen, containerRef]);
  
  // Update all drag handle positions directly via DOM
  const updateDragHandlePositions = useCallback(() => {
    if (!dragHandlesContainerRef.current) return;
    
    const handles = dragHandlesContainerRef.current.querySelectorAll('[data-task-id]');
    handles.forEach(handle => {
      const taskId = handle.dataset.taskId;
      const pos = nodePositions[taskId];
      if (!pos) return;
      
      // Position at top-left of node (with small offset)
      const screenPos = canvasToScreen(pos.x - 8, pos.y + 8);
      handle.style.left = `${screenPos.x}px`;
      handle.style.top = `${screenPos.y}px`;
    });
  }, [nodePositions, canvasToScreen]);
  
  const writeAllHandlePositions = useCallback(() => {
    updateHandlePositions();
    updateDecisionHandlePositions();
    updateEdgeButtonPositions();
    updateDragHandlePositions();
  }, [updateHandlePositions, updateDecisionHandlePositions, updateEdgeButtonPositions, updateDragHandlePositions]);
  const writeAllHandlePositionsRef = useRef(writeAllHandlePositions);
  writeAllHandlePositionsRef.current = writeAllHandlePositions;

  const settleHandles = useCallback(() => {
    if (handleIdleRef.current) {
      clearTimeout(handleIdleRef.current);
      handleIdleRef.current = null;
    }
    const layer = layerRef?.current;
    if (layer) {
      layer.style.transform = '';
      layer.style.removeProperty('--handle-inv');
    }
    writeAllHandlePositionsRef.current();
    settledPanRef.current = { ...panRef.current };
    settledZoomRef.current = zoomRef.current;
  }, [layerRef]);

  // Slide the whole overlay with one transform so the buttons stay on the cards.
  // Each button scales back by the same amount, so a zoom does not grow or
  // shrink the (+) and the options button. Positions are rewritten once the
  // wheel goes quiet.
  useEffect(() => {
    const canvas = containerRef?.current?.querySelector('.flowchart__canvas');
    if (!canvas) return;
    
    const handlePanUpdate = (e) => {
      const { x, y, zoom: currentZoom } = e.detail;
      panRef.current = { x, y };
      zoomRef.current = currentZoom;

      // One transform for the whole overlay. Rewriting every button on each
      // wheel tick is what made zoom feel heavy once many cards were on screen.
      const layer = layerRef?.current;
      if (layer) {
        const z0 = settledZoomRef.current || 1;
        const s = z0 ? currentZoom / z0 : 1;
        const tx = x - settledPanRef.current.x * s;
        const ty = y - settledPanRef.current.y * s;
        layer.style.transformOrigin = '0 0';
        layer.style.transform = `translate(${tx}px, ${ty}px) scale(${s})`;
        if (Math.abs(s - 1) > 0.0001) layer.style.setProperty('--handle-inv', String(1 / s));
        else layer.style.removeProperty('--handle-inv');
      }

      if (handleIdleRef.current) clearTimeout(handleIdleRef.current);
      handleIdleRef.current = setTimeout(settleHandles, PAN_HANDLE_IDLE_MS);
    };
    
    canvas.addEventListener('panupdate', handlePanUpdate);
    window.addEventListener('flowchartpanend', settleHandles);
    return () => {
      canvas.removeEventListener('panupdate', handlePanUpdate);
      window.removeEventListener('flowchartpanend', settleHandles);
      if (handleIdleRef.current) {
        clearTimeout(handleIdleRef.current);
        handleIdleRef.current = null;
      }
    };
  }, [containerRef, layerRef, settleHandles]);
  
  // Update positions when node positions/dimensions change
  useEffect(() => {
    updateHandlePositions();
    updateDecisionHandlePositions();
    updateDragHandlePositions();
  }, [nodePositions, nodeDimensions, updateHandlePositions, updateDecisionHandlePositions, updateDragHandlePositions, layoutSurfaceEpoch]);
  
  // Update edge button positions when edges change
  useEffect(() => {
    updateEdgeButtonPositions();
  }, [edges, updateEdgeButtonPositions]);
  
  return {
    handlesContainerRef,
    decisionHandlesContainerRef,
    edgeButtonsContainerRef,
    dragHandlesContainerRef,
    panRef,
    zoomRef,
    canvasToScreen,
  };
}

export default useHandlePositions;

